import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { LandingPageStats, ButtonConfig, RealtimeStats } from '../types';
import { apiService } from '../services/api';
import { BarChart3, TrendingUp, Target, MapPin, Zap } from 'lucide-react';

const Modal = styled.div`
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const Content = styled.div`
  background: white;
  padding: 24px;
  border-radius: 12px;
  max-width: 500px;
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;
`;

const Title = styled.h3`
  margin: 0 0 20px 0;
  font-size: 20px;
  text-align: center;
`;

const Section = styled.div`
  margin-bottom: 20px;
`;

const SectionTitle = styled.h4`
  margin: 0 0 12px 0;
  font-size: 16px;
  color: #666;
`;

const Stat = styled.div`
  margin: 6px 0;
  font-size: 14px;
  display: flex;
  justify-content: space-between;
`;


const CloseButton = styled.button`
  margin-top: 20px;
  padding: 10px 20px;
  background: #f0f0f0;
  border: none;
  border-radius: 6px;
  cursor: pointer;

  &:hover {
    background: #e0e0e0;
  }
`;

const ToggleContainer = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 20px;
  justify-content: center;
`;

const ToggleButton = styled.button<{ active: boolean }>`
  padding: 8px 16px;
  background: ${props => props.active ? '#007bff' : '#f8f9fa'};
  color: ${props => props.active ? 'white' : '#495057'};
  border: 1px solid #dee2e6;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;

  &:hover {
    background: ${props => props.active ? '#0056b3' : '#e9ecef'};
  }
`;

const TimeBar = styled.div<{ count: number; maxCount: number }>`
  height: 20px;
  background: #e0e0e0;
  border-radius: 10px;
  margin: 4px 0;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    left: 0; top: 0; bottom: 0;
    width: ${props => props.maxCount > 0 ? (props.count / props.maxCount * 100) : 0}%;
    background: #28a745;
    border-radius: 10px;
  }
`;


const MetricCard = styled.div`
  background: #f8f9fa;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 12px;
  border: 1px solid #e9ecef;
`;

const MetricGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 12px;
  margin-bottom: 20px;
`;

const MetricValue = styled.div`
  font-size: 24px;
  font-weight: bold;
  color: #007bff;
  margin-bottom: 4px;
`;

const MetricLabel = styled.div`
  font-size: 12px;
  color: #6c757d;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;


const PerformanceTable = styled.div`
  margin: 20px 0;
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr;
  gap: 12px;
  padding: 12px;
  background: #f8f9fa;
  border-radius: 8px;
  font-weight: 600;
  font-size: 14px;
  margin-bottom: 8px;
`;

const TableRow = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr;
  gap: 12px;
  padding: 12px;
  border-bottom: 1px solid #e9ecef;
  align-items: center;
`;

const Badge = styled.span<{ type: 'success' | 'warning' | 'danger' }>`
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  background: ${props => {
    switch (props.type) {
      case 'success': return '#d4edda';
      case 'warning': return '#fff3cd';
      case 'danger': return '#f8d7da';
      default: return '#e9ecef';
    }
  }};
  color: ${props => {
    switch (props.type) {
      case 'success': return '#155724';
      case 'warning': return '#856404';
      case 'danger': return '#721c24';
      default: return '#383d41';
    }
  }};
`;

const RealtimeIndicator = styled.div<{ online: number }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px;
  background: ${props => props.online > 0 ? '#d4edda' : '#f8f9fa'};
  border-radius: 8px;
  margin-bottom: 20px;
`;

const OnlineDot = styled.div<{ online: number }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${props => props.online > 0 ? '#28a745' : '#6c757d'};
  animation: ${props => props.online > 0 ? 'pulse 2s infinite' : 'none'};

  @keyframes pulse {
    0% { box-shadow: 0 0 0 0 rgba(40, 167, 69, 0.7); }
    70% { box-shadow: 0 0 0 10px rgba(40, 167, 69, 0); }
    100% { box-shadow: 0 0 0 0 rgba(40, 167, 69, 0); }
  }
`;

type TimePeriod = 'daily' | 'weekly' | 'monthly';

interface StatsModalProps {
  pageName: string;
  buttons: ButtonConfig[];
  stats: LandingPageStats;
  pageId: string;
  onClose: () => void;
}

// Helper function to get date key for grouping
const getDateKey = (date: Date, period: TimePeriod): string => {
  switch (period) {
    case 'daily':
      return date.toISOString().split('T')[0]; // YYYY-MM-DD
    case 'weekly':
      const weekStart = new Date(date);
      weekStart.setDate(date.getDate() - date.getDay()); // Start of week (Sunday)
      return weekStart.toISOString().split('T')[0];
    case 'monthly':
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`; // YYYY-MM
    default:
      return date.toISOString().split('T')[0];
  }
};

// Helper function to get display label for date key
const getDateLabel = (dateKey: string, period: TimePeriod): string => {
  const date = new Date(dateKey + (period === 'daily' ? 'T00:00:00' : period === 'monthly' ? '-01T00:00:00' : 'T00:00:00'));
  switch (period) {
    case 'daily':
      return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
    case 'weekly':
      const endDate = new Date(date);
      endDate.setDate(date.getDate() + 6);
      return `${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
    case 'monthly':
      return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short' });
    default:
      return dateKey;
  }
};

// Function to aggregate daily stats by time period
const aggregateByTimePeriod = (
  dailyStats: Array<{ date: string; visitors: number; pageViews: number; buttonClicks: number }>,
  period: TimePeriod,
  limit: number = period === 'daily' ? 7 : period === 'weekly' ? 4 : 3
): Record<string, { visitors: number; pageViews: number; buttonClicks: number }> => {
  const now = new Date();
  const cutoffDate = new Date();

  switch (period) {
    case 'daily':
      cutoffDate.setDate(now.getDate() - limit);
      break;
    case 'weekly':
      cutoffDate.setDate(now.getDate() - (limit * 7));
      break;
    case 'monthly':
      cutoffDate.setMonth(now.getMonth() - limit);
      break;
  }

  const filtered = dailyStats.filter(stat => new Date(stat.date) >= cutoffDate);
  const grouped: Record<string, { visitors: number; pageViews: number; buttonClicks: number }> = {};

  filtered.forEach(stat => {
    const date = new Date(stat.date);
    const key = getDateKey(date, period);
    if (!grouped[key]) {
      grouped[key] = { visitors: 0, pageViews: 0, buttonClicks: 0 };
    }
    grouped[key].visitors += stat.visitors;
    grouped[key].pageViews += stat.pageViews;
    grouped[key].buttonClicks += stat.buttonClicks;
  });

  return grouped;
};

// Function to calculate total metrics for a specific time period
const calculatePeriodTotals = (
  dailyStats: Array<{ date: string; visitors: number; pageViews: number; buttonClicks: number }>,
  period: TimePeriod
): { visitors: number; pageViews: number; buttonClicks: number } => {
  const now = new Date();
  const cutoffDate = new Date();

  switch (period) {
    case 'daily':
      cutoffDate.setDate(now.getDate() - 7); // Last 7 days
      break;
    case 'weekly':
      cutoffDate.setDate(now.getDate() - 28); // Last 4 weeks
      break;
    case 'monthly':
      cutoffDate.setMonth(now.getMonth() - 3); // Last 3 months
      break;
  }

  const filtered = dailyStats.filter(stat => new Date(stat.date) >= cutoffDate);

  return filtered.reduce(
    (totals, stat) => ({
      visitors: totals.visitors + stat.visitors,
      pageViews: totals.pageViews + stat.pageViews,
      buttonClicks: totals.buttonClicks + stat.buttonClicks,
    }),
    { visitors: 0, pageViews: 0, buttonClicks: 0 }
  );
};

const StatsModal: React.FC<StatsModalProps> = ({ pageName, buttons, stats, pageId, onClose }) => {
  const [realtimeStats, setRealtimeStats] = useState<RealtimeStats | null>(null);
  const [timePeriod, setTimePeriod] = useState<TimePeriod>('daily');

  // Fetch additional analytics data
  useEffect(() => {
    const fetchAdditionalStats = async () => {
      try {
        const realtime = await apiService.getRealtimeStats(pageId);
        setRealtimeStats(realtime as RealtimeStats);
      } catch (error) {
        console.error('Failed to fetch additional stats:', error);
      }
    };

    fetchAdditionalStats();
  }, [pageId]);

  // Create a map of button ID to button config for easy lookup
  const buttonMap = buttons.reduce((map, button) => {
    map[button.id] = button;
    return map;
  }, {} as Record<string, ButtonConfig>);

  // Calculate period-specific totals from daily stats
  const periodTotals = stats.dailyStats && stats.dailyStats.length > 0
    ? calculatePeriodTotals(stats.dailyStats, timePeriod)
    : { visitors: 0, pageViews: 0, buttonClicks: 0 };

  return (
    <Modal onClick={onClose}>
      <Content onClick={e => e.stopPropagation()}>
        <Title><BarChart3 size={24} style={{ marginRight: '12px' }} /> Performance Analytics for "{pageName}"</Title>

        {/* Time Period Toggle */}
        <ToggleContainer>
          <ToggleButton active={timePeriod === 'daily'} onClick={() => setTimePeriod('daily')}>
            Daily
          </ToggleButton>
          <ToggleButton active={timePeriod === 'weekly'} onClick={() => setTimePeriod('weekly')}>
            Weekly
          </ToggleButton>
          <ToggleButton active={timePeriod === 'monthly'} onClick={() => setTimePeriod('monthly')}>
            Monthly
          </ToggleButton>
        </ToggleContainer>

        {/* Real-time indicator */}
        <RealtimeIndicator online={realtimeStats?.currentOnline || 0}>
          <OnlineDot online={realtimeStats?.currentOnline || 0} />
          <span>
            <strong>{realtimeStats?.currentOnline || 0}</strong> people online now
            {realtimeStats?.recentActivity && realtimeStats.recentActivity.length > 0 && (
              <span> • {realtimeStats.recentActivity.length} active sessions</span>
            )}
          </span>
        </RealtimeIndicator>

        {/* Key Metrics Overview */}
        <MetricGrid>
          <MetricCard>
            <MetricValue>{periodTotals.visitors.toLocaleString()}</MetricValue>
            <MetricLabel>Unique Visitors</MetricLabel>
          </MetricCard>
          <MetricCard>
            <MetricValue>{periodTotals.pageViews.toLocaleString()}</MetricValue>
            <MetricLabel>Page Views</MetricLabel>
          </MetricCard>
          <MetricCard>
            <MetricValue>{periodTotals.buttonClicks.toLocaleString()}</MetricValue>
            <MetricLabel>Total Clicks</MetricLabel>
          </MetricCard>
          <MetricCard>
            <MetricValue>
              {periodTotals.pageViews > 0
                ? (periodTotals.buttonClicks / periodTotals.pageViews * 100).toFixed(1) + '%'
                : '0%'
              }
            </MetricValue>
            <MetricLabel>Click Rate</MetricLabel>
          </MetricCard>
        </MetricGrid>


        {/* Time-bucketed Stats */}
        {stats.dailyStats && stats.dailyStats.length > 0 && (
          <Section>
            <SectionTitle><TrendingUp size={18} style={{ marginRight: '8px' }} /> Activity Over Time ({timePeriod === 'daily' ? 'Last 7 Days' : timePeriod === 'weekly' ? 'Last 4 Weeks' : 'Last 3 Months'})</SectionTitle>
            {(() => {
              const timeData = aggregateByTimePeriod(stats.dailyStats, timePeriod);
              const maxViews = Math.max(...Object.values(timeData).map(d => d.pageViews), 1);
              return Object.entries(timeData)
                .sort(([a], [b]) => a.localeCompare(b))
                .map(([dateKey, data]) => (
                  <div key={dateKey}>
                    <Stat>
                      <span>{getDateLabel(dateKey, timePeriod)}:</span>
                      <strong>{data.pageViews} views, {data.buttonClicks} clicks</strong>
                    </Stat>
                    <TimeBar count={data.pageViews} maxCount={maxViews} />
                  </div>
                ));
            })()}
          </Section>
        )}

        {/* Button Performance */}
          <Section>
            <SectionTitle><Target size={18} style={{ marginRight: '8px' }} /> Button Performance</SectionTitle>
          {stats.buttonPerformance && stats.buttonPerformance.length > 0 ? (
            <PerformanceTable>
              <TableHeader>
                <span>Button / Destination</span>
                <span>Clicks</span>
                <span>Click Rate</span>
              </TableHeader>
              {stats.buttonPerformance.map((perf) => {
                const button = buttonMap[perf.buttonId];
                let buttonLabel = perf.buttonId;
                let destination = perf.buttonLink || 'N/A';

                // Handle special cases
                if (perf.buttonId === 'fallback_button') {
                  buttonLabel = 'Fallback Button';
                  destination = 'Fallback Modal';
                } else if (button) {
                  buttonLabel = `"${button.text}"`;
                  destination = button.link;
                }

                const clickRate = stats.totalPageViews && stats.totalPageViews > 0
                  ? (perf.totalClicks / stats.totalPageViews * 100)
                  : 0;
                return (
                  <TableRow key={perf.buttonId}>
                    <span>{buttonLabel} → {destination}</span>
                    <span>{perf.totalClicks.toLocaleString()}</span>
                    <Badge type={clickRate > 5 ? 'success' : clickRate > 2 ? 'warning' : 'danger'}>
                      {clickRate.toFixed(1)}%
                    </Badge>
                  </TableRow>
                );
              })}
            </PerformanceTable>
          ) : (
            <Stat><span>No button performance data yet</span></Stat>
          )}
        </Section>


        {/* Geographic Performance */}
        <Section>
          <SectionTitle><MapPin size={18} style={{ marginRight: '8px' }} /> Geographic Performance</SectionTitle>
          {stats.geographicData && stats.geographicData.length > 0 ? (
            <PerformanceTable>
              <TableHeader>
                <span>Location</span>
                <span>Visitors</span>
              </TableHeader>
              {stats.geographicData.slice(0, 5).map((geo, index) => (
                <TableRow key={index}>
                  <span>{geo.country || 'Unknown'}</span>
                  <span>{geo.visitorCount.toLocaleString()}</span>
                </TableRow>
              ))}
            </PerformanceTable>
          ) : (
            <Stat><span>No geographic data yet</span></Stat>
          )}
        </Section>

        {/* Recent Activity */}
        {realtimeStats?.recentActivity && realtimeStats.recentActivity.length > 0 && (
          <Section>
            <SectionTitle><Zap size={18} style={{ marginRight: '8px' }} /> Recent Activity</SectionTitle>
            {realtimeStats.recentActivity.slice(0, 3).map((activity, index) => (
              <Stat key={index}>
                <span>Visitor {activity.visitorId.slice(-8)}:</span>
                <span>{activity.sessionCount} sessions • Last seen {new Date(activity.lastSeen).toLocaleTimeString()}</span>
              </Stat>
            ))}
          </Section>
        )}

        <div style={{ textAlign: 'center' }}>
          <CloseButton onClick={onClose}>Close</CloseButton>
        </div>
      </Content>
    </Modal>
  );
};

export default StatsModal;
